import { Raycaster, Vector2 } from "three";
import { Viewer } from "../viewer";
import { MeasurementManager } from "./MeasurementManager";
import { PanelDragObservable } from "./PanelDragObservable";
import { IMeasurementHandler } from "./types";

export class MeasurementSelector {
  private pointerDown = false;

  constructor(
    private viewer: Viewer,
    private element: HTMLCanvasElement,
    private measurementManager: MeasurementManager,
    private panelDragObservable?: PanelDragObservable
  ) {
    this.registerEvents();
  }

  private get camera() {
    return this.viewer.getCamera();
  }

  private emitClickMeasurement = (measurement: any, event: MouseEvent) => {
    const customEvent = new CustomEvent("measurement-click", {
      detail: { measurement },
    });
    window.dispatchEvent(customEvent);
  };

  private emitRightClickMeasurement(measurement: any, event: PointerEvent) {
    const customEvent = new CustomEvent("measurement-right-click", {
      detail: {
        measurement,
        event,
      },
    });

    window.dispatchEvent(customEvent);
  }

  private registerEvents() {
    this.element.addEventListener("pointerdown", this.onPointerDown);
    this.element.addEventListener("pointermove", this.onPointerMove);
    this.element.addEventListener("click", this.onDoubleClick);
    window.addEventListener("pointerleave", this.onPointerUp);
    window.addEventListener("pointerup", this.onPointerUp);
  }
  private get isEnabled() {
    return !this.measurementManager.isMeasuring;
  }

  private onDoubleClick = (event: MouseEvent) => {
    if (!this.isEnabled) return;
    // Ignore the click fired right after a panel drag (no select/deselect)
    if (this.panelDragObservable?.suppressClick) return;
    const pointerEvent = event as unknown as PointerEvent;
    const measurements = this.getMeasurements(pointerEvent);
    const [measurement] = measurements;
    if (measurement) {
      this.emitClickMeasurement(measurement, event);
    }
  };

  private onPointerDown = (event: PointerEvent) => {
    if (!this.isEnabled && event.button !== 2) return;
    this.pointerDown = true;
    if (event.button === 2) {
      if (!this.measurementManager.isMeasuring) {
        const measurements = this.getMeasurements(event);
        const [measurement] = measurements;
        if (measurement) {
          this.emitRightClickMeasurement(measurement, event);
        }
      }
    }
  };

  private onPointerMove = (event: PointerEvent) => {
    if (!this.isEnabled) return;
    if (!this.pointerDown) {
      const measurements = this.getMeasurements(event);
      const [measurement] = measurements;
      if (measurement) {
        this.measurementManager.changeAppearanceByState(
          measurement?.id,
          "hovered"
        );
      } else {
        this.measurementManager.changeAppearanceByState(
          measurement?.id,
          "default"
        );
      }
    }
  };
  private onPointerUp = (event: PointerEvent) => {
    this.pointerDown = false;
  };

  private getMeasurements(event: PointerEvent | MouseEvent) {
    const raycaster = this.screenToRaycaster(event);
    const measurements: {
      measurement: IMeasurementHandler;
      distance: number;
    }[] = [];
    for (const measurement of this.measurementList) {
      if (measurement.getVisibility() === false) continue;
      const intersects = measurement.hitText?.(raycaster) || [];

      if (intersects.length > 0) {
        measurements.push({
          measurement,
          distance: intersects[0].distance,
        });
      }
    }
    return measurements
      .sort((a, b) => a.distance - b.distance)
      .map((m) => m.measurement);
  }

  private get measurementList() {
    return this.measurementManager.measurementMaps.values();
  }

  private screenToRaycaster(event: PointerEvent | MouseEvent) {
    const [x, y] = [event.offsetX, event.offsetY];
    const width = this.element.clientWidth;
    const height = this.element.clientHeight;
    const ndc = new Vector2((x / width) * 2 - 1, -(y / height) * 2 + 1);
    const camera = this.camera;

    const raycaster = new Raycaster();
    raycaster.setFromCamera(ndc, camera);
    return raycaster;
  }

  private unregisterEvents() {
    this.element.removeEventListener("pointerdown", this.onPointerDown);
  }
}
